import { useState, useEffect } from "react";
import { usePricing } from "@/contexts/PricingContext";
import { useVehicle, CATEGORY_LIST } from "@/contexts/VehicleContext";
import { Car, X, Check, ArrowRight, Hash } from "lucide-react";

export default function VehicleSelector() {
  const { selectorOpen, closeSelector, category, setCategory, plate, setPlate } = useVehicle();
  const { pricing } = usePricing();
  const [localPlate, setLocalPlate] = useState("");

  useEffect(() => {
    if (selectorOpen) setLocalPlate(plate || "");
  }, [selectorOpen, plate]);

  if (!selectorOpen) return null;

  const choose = (catId) => {
    setCategory(catId);
    setPlate(localPlate);
    closeSelector();
  };

  const handlePlateChange = (e) => {
    setLocalPlate(e.target.value.toUpperCase());
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fade-up"
      data-testid="vehicle-selector-modal"
      onClick={closeSelector}
    >
      <div
        className="relative w-full max-w-5xl max-h-[92vh] overflow-y-auto bg-[#0A0A0A] border border-white/10 rounded-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative px-6 sm:px-10 pt-8 pb-6 border-b border-white/10 overflow-hidden">
          <div className="absolute -top-24 -right-24 w-56 h-56 bg-[#E50914]/20 rounded-full blur-3xl"></div>

          <button
            onClick={closeSelector}
            data-testid="vehicle-selector-close"
            className="absolute top-5 right-5 w-10 h-10 rounded-full flex items-center justify-center bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all"
            aria-label="Fermer"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="relative">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#E50914]/10 border border-[#E50914]/40 rounded-full mb-4">
              <Car className="w-3.5 h-3.5 text-[#E50914]" />
              <span className="text-[10px] uppercase tracking-[0.25em] text-white font-bold">
                Identifiez votre véhicule
              </span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tighter mb-3">
              Quel est votre <span className="text-gradient-red">véhicule ?</span>
            </h2>
            <p className="text-white/60 text-sm sm:text-base max-w-2xl">
              Choisissez votre catégorie pour les tarifs de nettoyage. La plaque (optionnelle) nous aide à préparer votre intervention mécanique avec précision.
            </p>
          </div>
        </div>

        {/* Plate input */}
        <div className="px-6 sm:px-10 pt-8 pb-2">
          <label className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/60 font-bold mb-3">
            <Hash className="w-3.5 h-3.5 text-[#E50914]" />
            Plaque d'immatriculation <span className="font-medium text-white/40 normal-case tracking-normal">(optionnel — utile pour la mécanique)</span>
          </label>
          <input
            type="text"
            value={localPlate}
            onChange={handlePlateChange}
            placeholder="AB-123-CD"
            maxLength={10}
            data-testid="vehicle-plate-input"
            className="w-full sm:max-w-xs bg-[#050505] border border-white/15 rounded-lg px-5 py-4 text-white text-xl font-mono font-bold tracking-[0.2em] uppercase placeholder:text-white/20 placeholder:tracking-normal placeholder:font-normal focus:outline-none focus:border-[#E50914] transition-colors text-center"
          />
        </div>

        {/* Categories grid */}
        <div className="px-6 sm:px-10 pt-2 pb-6">
          <div className="text-xs uppercase tracking-[0.2em] text-white/60 font-bold mb-4 mt-4">
            Catégorie du véhicule <span className="text-[#E50914]">*</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {CATEGORY_LIST.map((c) => {
              const p = pricing.auto[c.id] || c.prices;
              const active = category === c.id;
              return (
                <button
                  key={c.id}
                  onClick={() => choose(c.id)}
                  data-testid={`vehicle-cat-${c.id}`}
                  className={`group relative text-left p-6 rounded-xl border transition-all duration-300 overflow-hidden
                    ${active
                      ? 'border-[#E50914] bg-[#E50914]/10 shadow-[0_0_28px_rgba(229,9,20,0.25)]'
                      : 'border-white/10 bg-[#050505] hover:border-[#E50914]/50 hover:-translate-y-1'
                    }`}
                >
                  <div className="absolute -top-10 -right-10 w-32 h-32 bg-[#E50914]/0 group-hover:bg-[#E50914]/15 rounded-full blur-2xl transition-colors"></div>

                  <div className="relative flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-14 h-14 rounded-lg flex items-center justify-center text-2xl font-black tracking-tighter
                        ${active ? 'bg-[#E50914] text-white' : 'bg-white/5 border border-white/10 text-[#E50914] group-hover:bg-[#E50914] group-hover:text-white'}
                        transition-all`}
                      >
                        {c.id}
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">{c.label}</div>
                        <div className="font-bold text-white text-lg leading-tight">{c.name}</div>
                      </div>
                    </div>
                    {active && (
                      <div className="w-7 h-7 rounded-full bg-[#E50914] flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" strokeWidth={3} />
                      </div>
                    )}
                  </div>

                  <p className="relative text-sm text-white/60 mb-5 italic">
                    Ex : {c.examples}
                  </p>

                  <div className="relative pt-4 border-t border-white/10">
                    {(!p.exterieur && !p.interieur && !p.pack && !p.concession) ? (
                      <div className="text-center py-2">
                        <div className="text-[10px] uppercase tracking-wider text-white/40 mb-0.5">Tarif</div>
                        <div className="text-lg font-black text-[#E50914]">Sur devis</div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-4 gap-2 text-center">
                        <PriceChip label="Ext." price={p.exterieur} />
                        <PriceChip label="Int." price={p.interieur} />
                        <PriceChip label="Pack" price={p.pack} />
                        <PriceChip label="Premium" price={p.concession} accent />
                      </div>
                    )}
                  </div>

                  <div className="relative mt-5 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#E50914] group-hover:gap-3 transition-all">
                    {active ? 'Sélectionné' : 'Choisir cette catégorie'} <ArrowRight className="w-3.5 h-3.5" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="px-6 sm:px-10 py-4 bg-black/40 border-t border-white/10 text-center text-xs text-white/50">
          Vous hésitez sur la catégorie ? Contactez-nous par WhatsApp ou téléphone, nous vous aiderons à choisir.
        </div>
      </div>
    </div>
  );
}

function PriceChip({ label, price, accent }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-white/40 mb-0.5">{label}</div>
      <div className={`text-sm font-bold ${accent ? 'text-[#F87171]' : 'text-white'}`}>{price}€</div>
    </div>
  );
}
